import DateFormat from '../lib.r2/utils/DateFormat'
import LocalCache from '../lib.r2/utils/LocalCache'
import StoreBase, { BaseStoreState } from './StoreBase'
import { NavigateFunction } from 'react-router-dom'

const parser: DOMParser = new DOMParser();		//xml parser for error messages

// sessionStorage key for the one-time 2FA enrollment token (from the e-mail link #/2fa/enroll?token=...)
export const TWO_FACTOR_ENROLLMENT_TOKEN_KEY = 'twoFactorEnrollmentToken'

// Password rules (NIST SP 800-63B) - only length is checked on the client, the backend checks known leaks
export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 128
export const PASSWORD_HINT = 'Mindestens 12 Zeichen, Passphrasen erlaubt, keine Pflicht für Sonderzeichen. Bekannte geleakte Passwörter werden abgelehnt.'

// backend error codes (field "errorMessage") -> German user messages
export const ERROR_MESSAGES: Record<string, string> = {
	RATE_LIMITED: 'Zu viele Versuche. Bitte später erneut versuchen.',
	'2FA_ALREADY_ENABLED': 'Die 2-Faktor-Authentifizierung ist bereits eingerichtet.',
	'2FA_ENROLLMENT_REQUIRED': 'Die Einrichtung der 2-Faktor-Authentifizierung ist nur über den persönlichen Link möglich, der Ihnen per E-Mail zugesendet wurde. Bitte wenden Sie sich an einen Administrator, um den Link erneut zu erhalten.',
	'2FA_ENROLLMENT_TOKEN_INVALID': 'Der Link zur Einrichtung der 2-Faktor-Authentifizierung ist ungültig, abgelaufen oder wurde bereits verwendet. Bitte fordern Sie bei einem Administrator einen neuen Link an.',
	'2FA_INVALID_CODE': 'Der eingegebene Code ist ungültig.',
	'2FA_REQUIRED': 'Bitte bestätigen Sie Ihre Anmeldung mit der 2-Faktor-Authentifizierung.',
	'2FA_SETUP_REQUIRED': 'Bitte richten Sie zuerst die 2-Faktor-Authentifizierung ein.',
	PASSWORD_TOO_SHORT: `Das Passwort ist zu kurz (mindestens ${PASSWORD_MIN_LENGTH} Zeichen).`,
	PASSWORD_TOO_LONG: `Das Passwort ist zu lang (höchstens ${PASSWORD_MAX_LENGTH} Zeichen).`,
	PASSWORD_COMPROMISED: 'Dieses Passwort ist aus bekannten Datenlecks bekannt und darf nicht verwendet werden. Bitte wählen Sie ein anderes Passwort.',
	FLIGHT_STARTS_BLOCKED: 'Flugstarts sind derzeit durch einen Administrator gesperrt.',
}

// translate a backend error code into a German message (fallback: raw message)
export function translateError(code: string | null | undefined, retryAfterSeconds?: number | null): string {
	if (!code) return 'Unbekannter Fehler'
	if (code === 'RATE_LIMITED' && retryAfterSeconds && retryAfterSeconds > 0) {
		const minutes = Math.ceil(retryAfterSeconds / 60)
		return `Zu viele Versuche. Bitte in ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'} erneut versuchen.`
	}
	return ERROR_MESSAGES[code] ?? code
}

// extract "errorMessage" from a JSON ({"errorMessage":"CODE"}) or XML (<errorMessage>CODE</errorMessage>) error body
function parseErrorCode(errorText: string | null | undefined): string | null {
	if (!errorText) return null
	try {
		const json = JSON.parse(errorText)
		if (json && typeof json === 'object') {
			return json.errorMessage ?? json.message ?? json.error ?? null
		}
	} catch (error) {
		//no json
	}
	try {
		const xmlDoc = parser.parseFromString(errorText, 'text/xml')
		const errorMessage = xmlDoc.querySelector('errorMessage')?.textContent
		if (errorMessage) return errorMessage
	} catch (error) {
		//no xml
	}
	return null
}

export interface FetchResult {
	ok: boolean
	status: number
	data: any
	errorCode: string | null		//raw backend code, e.g. PASSWORD_TOO_SHORT
	errorMessage: string | null		//German message for the user
}

export interface MonitoringAlert {
	id: number
	timestamp: string
	username: string
	action: string
	details: string | null
}

export interface EmergencyStatus {
	flightStartsBlocked: boolean
	flightStartsBlockedReason: string | null
	flightStartsBlockedBy: string | null
	flightStartsBlockedAt: string | null
	sessionsInvalidatedAt: string | null
	sessionsInvalidatedBy: string | null
	flightHubTokenReloadedAt: string | null
	flightHubTokenReloadedBy: string | null
}


interface BookingData {
	id?: string
	selectedServiceId: string | null    //step 1
	name: string
	mission: string
	purposes: any[]
	coordinates: Record<string, any>
	dateFrom: string | null
	dateTo: string | null
	propertyNumber: string | null
	fieldCultivation: string | null
	area: string
	information: string
	coordinateSystem?: string           //step 3 (mock, not sent to backend yet)
	gcpFileName?: string | null         //step 3 (mock, not sent to backend yet)
	invoice_creation?: {
		enabled: boolean
	}
	price: number
	resultTypes: any[]
}

interface Role {
	id: number
	name: string
}

interface User {
	firstName: string
	lastName: string
	username: string
	email: string
	roles: Role[]
	agentType?: string
	twoFactorEnabled?: boolean
	permissions?: { name: string }[]
	_token: string
}

interface Order {
	orderId: string
	name: string
	price: number
	dateFrom: string
	dateTo: string
	orderState: string
	submitted?: string
	resultAssets?: any[]
	flightHubTaskUuid?: string | null
	flightHubTaskName?: string | null
	flightHubTaskBeginAt?: string | null
	flightHubStatus?: string | null
	flightHubMediaStatus?: string | null
	flightHubMediaZipStatus?: string | null
	flightHubMediaZipBlobName?: string | null
	pix4dProjectId?: number | null
	pix4dDetailUrl?: string | null
	pix4dStatus?: string | null
	pix4dResultsStatus?: string | null
	_dateFrom?: number
	_dateTo?: number
	_completed?: boolean
	invoice_creation?: {
		enabled: boolean
	}
}

interface OrderUpload {
	_file: File
	fileName: string
	purpose: string
	resultType: string
	total: number
	progress: number
	_update?: (progress?: number | null) => void
}

type UserMode = 'PROVIDER' | 'CONSUMER' | 'EXTERN' | null

const bookingDefaults: BookingData = {
	id: '',
	name: '',
	selectedServiceId: null,
	mission: 'Landwirtschaft',
	purposes: [],
	coordinates: {},
	dateFrom: null,
	dateTo: null,
	propertyNumber: null,
	fieldCultivation: null,
	area: '',
	information: '',
	price: 10000,
	resultTypes: [],
}

// Define IconColors and IconColorType
const IconColors = {
	ok: 'bg-green-500',
	info: 'bg-blue-500',
	warning: 'bg-yellow-500',
	error: 'bg-red-500',
} as const

type IconColorType = keyof typeof IconColors

interface DroneTypeInfo {
	id: number
	name: string
	reachDiameter: number
	flightSpeed: number
	startStopTime: number
	maxFlightTime: number
	fhModelKey?: string
	fhDomain?: string
	fhType?: string
	fhSubType?: string
	fhDeviceClass?: string
}

interface DroneInfo {
	id: number
	name: string
	serialNumber?: string
	callsign?: string
	deviceOnlineStatus?: boolean
	modeCode?: number
	droneType: DroneTypeInfo
}

interface FlightHubDroneModelInfo {
	key: string
	domain: string
	type: string
	subType: string
	name: string
	deviceClass: string
}

interface FlightHubDroneInfo {
	sn: string
	callsign: string
	online: boolean
	modeCode?: number
	deviceModel?: FlightHubDroneModelInfo
}

interface AvailableDroneSn extends FlightHubDroneInfo { }



interface GatewayModelInfo {
	key: string
	domain: string
	type: string
	subType: string
	name: string
	deviceClass: string
}

interface GatewayCameraInfo {
	cameraIndex: string
	cameraPosition: string
}

interface GatewayInfo {
	sn: string
	callsign: string
	online: boolean
	modeCode?: number
	deviceModel?: GatewayModelInfo
	cameraList?: GatewayCameraInfo[]
}

interface StoreState extends BaseStoreState {
	userMode: UserMode
	mode: UserMode
	orders: Order[]
	ordersError: string
	users: User[]
	usersError: string
	orderUploads: OrderUpload[]
	selectedOrder: Order | null
	hoveredOrder: string | null
	masterdata: any
	timerOrders: NodeJS.Timeout | null
	cache: LocalCache | null
	config: any
	latestOrderId: string | null
	user: User | null
	token: string | null
	twoFactorRequired: boolean
	twoFactorVerified: boolean
	features: {}
	geoMeshes: any[]
	geoMode: 'create' | 'edit'
	geoCenter: any | null
	snappedCenter: any | null
	geoJSON: any
	polygonRadius: number
	drones: DroneInfo[]
	selectedDrone: DroneInfo | null
	availableGateways: GatewayInfo[]
	availableDroneSns: AvailableDroneSn[]
	flightStartsBlocked: boolean
	flightStartsBlockedReason: string | null
}

const FIXED_POLYGON_RADIUS = 250

class Store extends StoreBase<StoreState> {
	headers: Record<string, string> = {
		'Content-Type': 'application/json',
	}

	navigate: NavigateFunction = () => { }
	setPopupMessage: (
		type: IconColorType,
		title: string,
		message: string,
		timeout?: number
	) => void = () => { }
	setPopupDialog: (content: any) => void = () => { }

	mode: UserMode = null
	bookingData: BookingData | null = null
	latestOrderId: string | null = null
	user: User | null = null
	token: string | null = null
	masterdata: any = null
	timerOrders: NodeJS.Timeout | null = null
	cache: LocalCache | null = null
	config: any = null
	private initPromise: Promise<void> | null = null
	private didInit = false

	types: Record<string, UserMode> = {
		ROLE_CONSUMER: 'CONSUMER',
		ROLE_PROVIDER: 'PROVIDER',
		ROLE_EXTERN: 'EXTERN',
	}

	// async init(navigate: NavigateFunction): Promise<void> {
	async init(): Promise<void> {
		if (this.didInit) return
		if (this.initPromise) return this.initPromise

		this.initPromise = this.initStore()
			.then(() => {
				this.didInit = true
			})
			.finally(() => {
				this.initPromise = null
			})

		return this.initPromise
	}

	private async initStore(): Promise<void> {
		const isLoginWithIdAustria = window.location.href.includes(
			'id-austria-login-successful'
		)
		console.log('isLoginWithIdAustria:', isLoginWithIdAustria)

		// this.navigate = navigate
		// navigate('loading')

		LocalCache.useSessionStorrage = true

		this.config = (global as any).config
		await super.init(this.config)

		await this.cache?.setItem('version', this.config.version)

		let user = sessionStorage.getItem('user')
		if (user) {
			try {
				const userData = JSON.parse(user) as User
				if (userData._token) {
					this.setToken(userData._token)

					this.set({
						isOnline: true,
						isLoaded: true,
						orders: [],
						ordersError: '',
						users: [],
						usersError: '',
						selectedOrder: null,
						features: {},
						polygonRadius: FIXED_POLYGON_RADIUS,
						geoCenter: null,
						snappedCenter: null,
						geoMeshes: [],
						geoMode: 'edit',
						geoJSON: null, //reset geoJSON
						drones: [],
						selectedDrone: null,
						availableGateways: [],
						availableDroneSns: [],
						twoFactorRequired: false,
						twoFactorVerified: false,
					})
					return this.onLoginSuccess(userData, true)
				}
			} catch (error) {
				console.error(
					'something wrong on loading the user data from session storage'
				)
			}
		}

		this.set({
			isOnline: true,
			isLoaded: true,
			isLoggedIn: false,
			orders: [],
			loginError: '',
			ordersError: '',
			users: [],
			usersError: '',
			selectedOrder: null,
			isLoginWithIdAustria,
			features: {},
			polygonRadius: FIXED_POLYGON_RADIUS,
			snappedCenter: null,
			twoFactorRequired: false,
			twoFactorVerified: false,
		})

		console.log('---init store done!', this)

		if (isLoginWithIdAustria) {
			this.onIDAustriaLogin()
		}
	}

	// ------------------------------------------------------------------------ fetch
	// autoLogout if 403 (Forbidden)
	async fetch(
		url: string,
		options?: RequestInit | string,
		autoLogout: boolean = false,
		showErrors: boolean = true

	): Promise<any> {
		const result = await this.fetchResult(url, options, showErrors, autoLogout)

		if (result.status === 403 && autoLogout) {
			//forbidden
			return this.onLogout()
		}

		return result.ok ? result.data : null
	}

	// ------------------------------------------------------------------------ fetchResult
	// like fetch, but returns status + backend error code/message (e.g. to show errors inside a form)
	async fetchResult(
		url: string,
		options?: RequestInit | string,
		showErrors: boolean = true,
		autoLogout: boolean = false

	): Promise<FetchResult> {
		if (typeof options === 'string') options = { method: options }

		const path = url
		url = this.config.api.url + url

		const headers: Record<string, string> = {
			...this.headers,
			...((options?.headers as Record<string, string>) ?? {}),
		}
		// Let the browser set multipart/form-data (with boundary) for uploads.
		if (options?.body instanceof FormData) delete headers['Content-Type']

		const fetchOptions: RequestInit = {
			method: 'GET',
			credentials: 'include',
			...options, //overwrite default settings
			headers,
		}

		let response: any = await fetch(url, fetchOptions).catch(err => err)

		if (!response || typeof response.status !== 'number' || typeof response.text !== 'function') {
			//network error (no response)
			console.error('request error:', response)
			const errorMessage = 'Der Server ist nicht erreichbar. Bitte später erneut versuchen.'
			if (showErrors) this.setPopupMessage('error', 'Fehler', errorMessage)
			return { ok: false, status: 0, data: null, errorCode: null, errorMessage }
		}

		if (response.status === 403 && autoLogout) {
			return { ok: false, status: 403, data: null, errorCode: null, errorMessage: null }
		}

		if (!response.ok) {
			console.error('request error:', response)

			let errorText: string | null = null
			try {
				errorText = await response.text()
			} catch (error) {
				errorText = null
			}

			const errorCode = parseErrorCode(errorText)
			const retryAfter = parseInt(response.headers.get('Retry-After') ?? '', 10)
			const errorMessage = errorCode
				? translateError(errorCode, isNaN(retryAfter) ? null : retryAfter)
				: response.status === 429
					? translateError('RATE_LIMITED', isNaN(retryAfter) ? null : retryAfter)
					: 'request error'

			console.log('errorMessage:', errorCode, errorMessage)

			// backend requires (verified) 2FA for this request -> go to the 2FA page (e.g. admins, providers)
			if ((errorCode === '2FA_REQUIRED' || errorCode === '2FA_SETUP_REQUIRED') && this.user && !path.startsWith('2fa')) {
				this.set({ twoFactorRequired: true, twoFactorVerified: false })
				this.navigate('/2fa')
			}

			if (showErrors) {
				this.setPopupMessage('error', `Fehler ${response.status}`, errorMessage)
			}

			return { ok: false, status: response.status, data: null, errorCode, errorMessage }
		}

		if (fetchOptions.method === 'DELETE') return { ok: true, status: response.status, data: true, errorCode: null, errorMessage: null } //successful delete

		if (!this.token) this.setToken(response.headers.get('x-csrf-token')) //set token from session storage

		let data: any = null
		try {
			const text = await response.text()
			if (text) {
				try {
					data = JSON.parse(text)
				} catch (error) {
					data = text //no json data
				}
			}
		} catch (error) {
			data = null
		}

		return { ok: true, status: response.status, data, errorCode: null, errorMessage: null }
	}

	// ------------------------------------------------------------------------ hasPermission
	// permissions of the logged in user, e.g. 'administration'
	hasPermission(name: string): boolean {
		return Array.isArray(this.user?.permissions) &&
			this.user!.permissions!.some(p => p?.name === name)
	}

	// ------------------------------------------------------------------------ hasRole
	// roles of the logged in user, e.g. 'ROLE_SUPER_ADMIN' (roles may come as string or array)
	hasRole(name: string): boolean {
		const roles = this.user?.roles as unknown
		if (Array.isArray(roles)) return roles.some(r => (typeof r === 'string' ? r : r?.name) === name)
		if (typeof roles === 'string') return roles.split(',').map(r => r.trim()).includes(name)
		return false
	}

	// emergency functions and monitoring alerts are restricted to super admins (backend enforces the same)
	isSuperAdmin(): boolean {
		return this.hasRole('ROLE_SUPER_ADMIN')
	}

	// ------------------------------------------------------------------------ setToken
	setToken(token_session: string | null): string | null {
		if (!token_session) {
			console.error('no token found!')
			return null
		}

		console.log('set token:', token_session)
		this.token = token_session
		this.headers['x-csrf-token'] = token_session
		sessionStorage.setItem('token', token_session)

		return token_session
	}

	// ------------------------------------------------------------------------ session
	// from onLogin
	async fetchSession(): Promise<string | null> {
		console.log('---session_for_login ...')

		const option_session: RequestInit = {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
			credentials: 'include',
		}

		let url_session = this.config.api.url + 'session'
		let response_session = await fetch(url_session, option_session).catch(
			err => err
		)

		if (!response_session?.ok) return null

		let token = this.setToken(response_session.headers.get('x-csrf-token')) //set token from session storage

		return token
	}

	// ------------------------------------------------------------------------ onLogin
	// from LoginPage
	async onLogin(data: { username: string; password: string }): Promise<void> {
		console.log('login', data.username, '...')

		this.set('loginError', '')

		await this.fetchSession() //get session token

		const result = await this.fetchResult('login', {
			method: 'POST',
			body: JSON.stringify(data),
		}, false)
		let user = result.data
		console.table(user)

		if (!result.ok && result.errorCode) {
			// e.g. RATE_LIMITED (429) -> "Zu viele Versuche. Bitte in X Minuten erneut versuchen."
			this.set('loginError', result.errorMessage ?? 'Fehler bei der Anmeldung')
		}
		else if (user?.errorMessage) this.set('loginError', translateError(user.errorMessage))
		else if (user?.username && user?.roles) {
			user._token = this.token
			sessionStorage.setItem('user', JSON.stringify(user))
			this.onLoginSuccess(user) //-> this.navigate('dashboard')
		} else {
			this.set('loginError', 'Fehler bei der Anmeldung')
			this.onLogout()
		}
	}

	// ------------------------------------------------------------------------ onIDAustriaLogin
	// from LoginPage
	async onIDAustriaLogin(): Promise<void> {
		console.log('login with id-austria ...')
		this.set('loginError', '')

		let user = await this.fetch('user')
		console.table(user)

		if (user?.errorMessage) this.set('loginError', user.errorMessage)
		else if (user?.username && user?.roles) {
			user._token = this.token
			sessionStorage.setItem('user', JSON.stringify(user))
			this.onLoginSuccess(user) //-> this.navigate('dashboard')
		} else {
			this.set('loginError', 'Fehler bei der Anmeldung')
			this.onLogout()
		}
	}

	// ------------------------------------------------------------------------ onLoginSuccess
	async onLoginSuccess(user: User, skipNavigateIfOnPage = false): Promise<void> {
		console.log('---onLoginSuccess:', user)
		this.user = user
		this.token = user._token
		this.headers['x-csrf-token'] = user._token

		this.mode =
			(user.agentType as UserMode) ?? this.types[user.roles[0]?.name]
		console.log('user mode:', this.mode)

		this.setToken(user._token)

		const twoFactorStatus = await this.fetch('2fa', undefined, false, false)
		const required = !!twoFactorStatus?.twoFactorRequired
		const verified = !!twoFactorStatus?.twoFactorVerified

		this.set({
			isLoggedIn: true,
			isLoginWithIdAustria: false,
			mode: this.mode,
			userMode: this.mode,
			twoFactorRequired: required,
			twoFactorVerified: verified,
		})

		if ((this.mode === 'PROVIDER' || this.mode === 'CONSUMER') && (!required || verified)) {
			this.masterdata = await this.fetch('masterdata') //load masterdata
			console.log('masterdata:', this.masterdata)
		}

		// note: 2FA is checked for every user mode (providers, admins, super admins ...), not only PROVIDER
		// the flight start banner (SystemStatusBanner) loads 'system-status' once isLoggedIn/2FA are ok

		if (!skipNavigateIfOnPage) {
			this.navigate(required && !verified ? '/2fa' : '/dashboard')
		}
	}

	// ------------------------------------------------------------------------ getRoleLabel
	getRoleLabel(): string {
		if (!this.user) return ''
		const roles: string[] = Array.isArray(this.user.roles)
			? (this.user.roles as any[]).map(r => typeof r === 'string' ? r : r.name)
			: []
		if (roles.includes('ROLE_SUPER_ADMIN')) return 'Super Admin'
		if (roles.includes('ROLE_ADMIN')) return 'Admin'
		if (roles.includes('ROLE_PROVIDER')) return 'Anbieter'
		if (roles.includes('ROLE_CONSUMER')) return 'Bürger'
		if (roles.includes('ROLE_EXTERN')) return 'Extern'
		return ''
	}

	// ------------------------------------------------------------------------ onLogout
	// from UserMenu
	//
	onLogout(): void {
		console.log('onLogOut!')
		if (this.timerOrders) clearTimeout(this.timerOrders)

		this.set({
			orders: [],
			selectedOrder: null,
			isLoggedIn: false,
			isLoginWithIdAustria: false,
			twoFactorRequired: false,
			twoFactorVerified: false,
			flightStartsBlocked: false,
			flightStartsBlockedReason: null,
		})

		this.token = null
		this.user = null
		this.mode = null

		sessionStorage.removeItem('user') //remove the current user session
		sessionStorage.removeItem('token') //remove the current user session

		fetch(this.config.api.url + 'login', {
			method: 'DELETE',
			headers: this.headers,
			credentials: 'include',
		}).catch(err => err)

		delete this.headers['x-csrf-token']

		this.navigate('login')
	}

	// ------------------------------------------------------------------------ loadOrders
	async loadOrders(): Promise<void> {
		console.debug('---loadOrders')
		if (this.timerOrders) clearTimeout(this.timerOrders)

		let orders = await this.fetch(
			this.mode === 'PROVIDER' ? 'provider/orders' : 'consumer/orders',
			undefined,
			true
		)
		// console.debug('orders:', orders)

		if (orders?.error)
			return this.set({ ordersError: JSON.stringify(orders) })

		if (!orders) return this.set({ ordersError: 'error on loading orders' })

		orders.forEach((order: Order) => {
			order._dateFrom = new Date(order.dateFrom).getTime()
			order._dateTo = new Date(order.dateTo).getTime()
		})

		orders.sort((a: Order, b: Order) => {
			if (a.submitted && b.submitted) return b.submitted.localeCompare(a.submitted)
			if (a.submitted) return -1
			if (b.submitted) return 1
			return (b._dateFrom ?? 0) - (a._dateFrom ?? 0)
		})

		this.set({ orders })

		if (
			orders.find(
				(o: Order) =>
					o.orderState.includes('PENDING') ||
					o.orderState.includes('AWAITING')
			)
		) {
			//some orders are pending
			// console.log('start update timer for pending orders (10 sec.) ...')

			if (this.timerOrders !== null) clearTimeout(this.timerOrders)
			this.timerOrders = setTimeout(() => this.loadOrders(), 10000) //update orders in 10 seconds
		}
	}


	async loadUsers(): Promise<User[] | null> {
		try {
			const users = await this.fetch('admin/users')

			if (users?.error) {
				console.error('Error loading users:', users.error)
				this.set({ usersError: users.error })
				return null
			}

			if (!users) {
				this.set({ usersError: 'No users found' })
				return null
			}

			this.set({ users })
			return users

		} catch (err) {
			console.error('Exception while loading users:', err)
			this.set({ usersError: String(err) })
			return null
		}
	}

	// returns the created user or the (German) backend error message, e.g. PASSWORD_COMPROMISED
	async createUser(newUser: User): Promise<{ user: User | null, error: string | null }> {
		try {
			const result = await this.fetchResult('admin/users', {
				method: 'POST',
				body: JSON.stringify(newUser),
				headers: { ...this.headers, 'Content-Type': 'application/json' },
			}, false);

			if (!result.ok) {
				const error = result.errorMessage ?? 'Fehler beim Anlegen des Benutzers'
				console.error('Error creating user:', result.errorCode);
				this.set({ usersError: error });
				return { user: null, error };
			}

			await this.loadUsers();

			return { user: result.data, error: null };
		} catch (err) {
			console.error('Exception while creating user:', err);
			this.set({ usersError: String(err) });
			return { user: null, error: String(err) };
		}
	}

	// returns the updated user or the (German) backend error message, e.g. PASSWORD_TOO_SHORT
	async updateUser(username: string, updatedUser: User): Promise<{ user: User | null, error: string | null }> {
		try {
			const result = await this.fetchResult(`admin/users/${encodeURIComponent(username)}`, {
				method: 'PUT',
				body: JSON.stringify(updatedUser),
				headers: { ...this.headers, 'Content-Type': 'application/json' },
			}, false);

			if (!result.ok) {
				const error = result.errorMessage ?? 'Fehler beim Speichern des Benutzers'
				console.error('Error updating user:', result.errorCode);
				this.set({ usersError: error });
				return { user: null, error };
			}

			await this.loadUsers();

			return { user: result.data, error: null };
		} catch (err) {
			console.error('Exception while updating user:', err);
			this.set({ usersError: String(err) });
			return { user: null, error: String(err) };
		}
	}

	// ------------------------------------------------------------------------ 2FA (admin)
	// (re)send the one-time 2FA enrollment link to a user (the old link becomes invalid)
	async sendTwoFactorEnrollmentLink(username: string): Promise<FetchResult> {
		return this.fetchResult(`admin/users/${encodeURIComponent(username)}/2fa/enrollment-link`, { method: 'POST' })
	}

	// reset the 2FA of a user; the backend automatically mails a new enrollment link
	async resetUserTwoFactor(username: string): Promise<FetchResult> {
		return this.fetchResult(`admin/users/${encodeURIComponent(username)}/2fa/reset`, { method: 'POST' })
	}

	// one-time rollout: send links to all users who must use 2FA but have not set it up -> {sent: n}
	async rolloutTwoFactorEnrollmentLinks(): Promise<FetchResult> {
		return this.fetchResult('admin/2fa/enrollment-links/rollout', { method: 'POST' })
	}

	// ------------------------------------------------------------------------ 2FA (user)
	// redeem the enrollment token from the e-mail link for the current session (removes the stored token)
	async enrollTwoFactor(token: string): Promise<FetchResult> {
		const result = await this.fetchResult('2fa/enroll', {
			method: 'POST',
			body: JSON.stringify({ token }),
		}, false)
		sessionStorage.removeItem(TWO_FACTOR_ENROLLMENT_TOKEN_KEY)
		return result
	}

	// ------------------------------------------------------------------------ emergency (admin)
	async loadEmergencyStatus(): Promise<FetchResult> {
		const result = await this.fetchResult('admin/emergency')
		if (result.ok && result.data) this.applyFlightStartStatus(result.data)
		return result
	}

	async blockFlightStarts(reason: string): Promise<FetchResult> {
		const result = await this.fetchResult('admin/emergency/flight-starts/block', {
			method: 'POST',
			body: JSON.stringify({ reason }),
		})
		if (result.ok && result.data) this.applyFlightStartStatus(result.data)
		return result
	}

	async unblockFlightStarts(): Promise<FetchResult> {
		const result = await this.fetchResult('admin/emergency/flight-starts/unblock', { method: 'POST' })
		if (result.ok && result.data) this.applyFlightStartStatus(result.data)
		return result
	}

	async reloadFlightHubToken(): Promise<FetchResult> {
		return this.fetchResult('admin/emergency/flighthub-token/reload', { method: 'POST' })
	}

	// last 50 security monitoring alerts (unexpected flights, unusual flight tasks)
	async loadMonitoringAlerts(): Promise<FetchResult> {
		return this.fetchResult('admin/emergency/alerts')
	}

	// last 200 entries of the security audit log (logins, 2FA, user administration, emergency actions)
	async loadAuditLog(): Promise<FetchResult> {
		return this.fetchResult('admin/emergency/audit')
	}

	// invalidates ALL sessions, including the own one -> log out locally afterwards
	async invalidateAllSessions(): Promise<FetchResult> {
		const result = await this.fetchResult('admin/emergency/sessions/invalidate', { method: 'POST' })
		if (result.ok) this.onLogout()
		return result
	}

	// ------------------------------------------------------------------------ system status
	// flight start block for the banner (any authenticated user), errors are not shown
	async loadSystemStatus(): Promise<void> {
		const result = await this.fetchResult('system-status', undefined, false)
		if (result.ok && result.data && typeof result.data === 'object') this.applyFlightStartStatus(result.data)
	}

	private applyFlightStartStatus(status: { flightStartsBlocked?: boolean, flightStartsBlockedReason?: string | null }): void {
		this.set({
			flightStartsBlocked: !!status.flightStartsBlocked,
			flightStartsBlockedReason: status.flightStartsBlockedReason ?? null,
		})
	}

	async deleteUser(username: string): Promise<boolean> {
		try {
			const res = await this.fetch(`admin/users/${username}`, {
				method: 'DELETE',
			});

			if (res?.error) {
				console.error('Error deleting user:', res.error);
				this.set({ usersError: res.error });
				return false;
			}

			await this.loadUsers();

			return true;
		} catch (err) {
			console.error('Exception while deleting user:', err);
			this.set({ usersError: String(err) });
			return false;
		}
	}

	// ------------------------------------------------------------------------ loadSearchOrders
	async loadSearchOrders(keyword: string): Promise<void> {
		console.log('---loadSearchOrders', keyword)

		this.set({ ordersError: '', orders: [] })

		let orders = await this.fetch(
			'search/orders?keyword=' + keyword,
			undefined,
			true
		)
		console.log('orders:', orders)

		if (orders?.error)
			return this.set({ ordersError: JSON.stringify(orders) })

		if (!orders)
			return this.set({
				orders: [],
				ordersError: 'keine orders gefunden',
			})

		orders.forEach((order: Order) => {
			order._dateFrom = new Date(order.dateFrom).getTime()
			order._dateTo = new Date(order.dateTo).getTime()
		})

		this.set({ ordersError: '', searchOrders: orders })
	}

	// ------------------------------------------------------------------------ loadSearchWithin
	// e.g. ../search/within?longitude=16.259705&latitude=48.161747&distance=5000
	//
	async loadSearchWithin(
		lat: number,
		lng: number,
		distance: number
	): Promise<void> {
		console.log('---loadSearchWithin', lat, lng, distance)

		this.set({
			searchOrdersError: '',
			// searchOrders:null
		})

		let url =
			'search/within?longitude=' +
			lng +
			'&latitude=' +
			lat +
			'&distance=' +
			distance
		let orders = await this.fetch(url, undefined, true, false)
		console.log('searchOrders:', orders)

		if (orders?.error)
			return this.set({ ordersError: JSON.stringify(orders) })

		if (!orders?.length)
			return this.set({
				searchOrders: [],
				searchOrdersError:
					'Keine Drohnendaten in diesem Bereich gefunden',
			})

		orders.forEach((order: Order) => {
			order._dateFrom = new Date(order.dateFrom).getTime()
			order._dateTo = new Date(order.dateTo).getTime()
		})

		this.set({ searchOrdersError: '', searchOrders: orders })
	}

	// ------------------------------------------------------------------------ selectOrder
	// from DashboardPage or OrdersPage
	async selectOrder(order: Order): Promise<void> {
		console.log('---select order:', this.mode, order)
		this.set({
			selectedOrder: order,
			ordersError: '',
			orderResults: [],
			orderUploads: [],
		})

		if (this.mode === 'CONSUMER') {
			await this.getResults(order.orderId) //-> set orderResults
		} else if (order.resultAssets) {
			//PROVIDER or EXTERN
			this.set({ orderUploads: order.resultAssets })
		} else {
			await this.getUploads(order.orderId) //-> set orderUploads
		}

		this.navigate('order/' + encodeURIComponent(order.orderId))
	}

	// ------------------------------------------------------------------------ selectOrderMap
	async selectOrderMap(order: Order): Promise<void> {
		console.log('---select order:', this.mode, order)
		this.set({ selectedOrder: order, ordersError: '' })

		if (order.resultAssets) {
			this.set({ orderUploads: order.resultAssets })
		} else {
			await this.getUploads(order.orderId) //-> set orderUploads
		}

		this.navigate('order/' + encodeURIComponent(order.orderId))
	}

	async refreshSelectedOrder(orderId: string): Promise<Order | null> {
		if (!orderId) return null
		const order = await this.fetch('provider/order?orderId=' + orderId, undefined, true)
		if (!order || order.error) return null
		this.set({ selectedOrder: order })
		return order as Order
	}

	async selectOrderById(orderId: string): Promise<boolean> {
		if (!orderId) return false

		const currentOrder = this.get('selectedOrder')
		if (currentOrder?.orderId === orderId) return true

		if (!this.get('orders')?.length) {
			await this.loadOrders()
		}

		const order = this.get('orders')?.find((item: Order) => item.orderId === orderId)
		if (!order) return false

		this.set({
			selectedOrder: order,
			ordersError: '',
			orderResults: [],
			orderUploads: [],
		})

		if (this.mode === 'CONSUMER') {
			await this.getResults(order.orderId)
		} else if (order.resultAssets) {
			this.set({ orderUploads: order.resultAssets })
		} else {
			await this.getUploads(order.orderId)
		}

		return true
	}

	// ------------------------------------------------------------------------ changeOrderState
	async changeOrderState(
		orderId: string,
		newOrderState: string
	): Promise<any> {
		let options: RequestInit = {
			method: 'PUT',
			credentials: 'include', //'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'x-csrf-token': this.token ?? '',
			},
		}

		let url =
			this.config.api.url +
			'provider/order?orderId=' +
			orderId +
			'&orderState=' +
			newOrderState
		console.log('---changeOrderState:', newOrderState, url, options, '...')

		let response = await fetch(url, options).catch(err => err)
		if (response?.ok) {
			let json = await response.json()

			await this.loadOrders() //reload orders
			return json
		} else return null
	}

	// ------------------------------------------------------------------------ removeOrder
	// from OrderPage
	async removeOrder(order: Order, mode: string): Promise<boolean | null> {
		let url =
			mode + '/order?orderId=' + order.orderId + '&orderState=CANCELED'

		console.log('remove order:', order, url, '...')
		let result = await this.fetch(url, { method: 'DELETE' }, true)
		console.log('result:', result) //result = true if successful

		setTimeout(() => {
			this.loadOrders() //reload orders
		}, 2000)

		return result
	}

	// ------------------------------------------------------------------------ getOrderAction
	getOrderAction(action: string): { title: string; text: string } | null {
		const result: { title: string; text: string } = {
			title: '',
			text: '',
		}

		switch (action) {
			case 'DECLINED':
				result.title = 'Der Auftrag wurde abgelehnt!'
				result.text =
					'Alles erledigt. Der Kunde wird über den Auftragstatus und den Grund für die Ablehnung benachrichtigt.'
				break

			case 'ACCEPTED':
				result.title = 'Der Auftrag wurde akzeptiert!'
				result.text =
					'Alles erledigt. Der Kunde wird über den Auftragstatus benachrichtigt. Sie können den Auftrag abschließen, wenn die Daten- und Analytics-Dateien bereit sind.'
				break

			case 'COMPLETED':
				result.title = 'Der Auftrag wurde abgeschlossen!'
				result.text =
					'Glückwunsch! Alles erledigt. Der Kunde wird über den Abschluss des Auftrages benachrichtigt.'
				break

			case 'DELAYED':
				result.title = 'Der Auftrag wurde verzögert!'
				result.text =
					'Alles erledigt. Der Kunde wird über die Verzögerung und den neuen Zeitrahmen benachrichtigt.'
				break

			default:
				return null
		}

		return result
	}

	// ------------------------------------------------------------------------ startBooking
	setBookingData(data: Partial<BookingData>): void {
		if (!this.bookingData) {
			this.bookingData = JSON.parse(JSON.stringify(bookingDefaults))
		}

		this.bookingData = {
			...this.bookingData,
			...data,
		} as BookingData
	}

	startBooking(): void {
		let to = new Date()
		to.setDate(to.getDate() + 30)

		this.bookingData = JSON.parse(JSON.stringify(bookingDefaults)) //create copy
		if (this.bookingData) {
			this.bookingData.dateFrom = DateFormat(new Date(), 'yyyy-mm-dd')
			this.bookingData.dateTo = DateFormat(to, 'yyyy-mm-dd')
		}
		console.info('---start booking!', this.bookingData)

		this.navigate('booking')
	}

	// ------------------------------------------------------------------------ goTo
	// refershe = navigate(0)
	// window.location.assign(target);
	goTo(target: string): void {
		console.log('---goto:', target, '...')
		this.navigate(target)
	}

	// ------------------------------------------------------------------------ onBack
	// back-button on header clicked
	onBack(to?: string): void {
		if (typeof to === 'string') {
			this.navigate(to)
		} else {
			this.get().isLoggedIn
				? this.navigate(-1 as any)
				: this.navigate('login')
		}
	}

	// ------------------------------------------------------------------------ goHome
	goHome(): void {
		console.log('---goHome')
		this.navigate(this.get().isLoggedIn ? 'dashboard' : 'login')
	}

	// ------------------------------------------------------------------------ booking
	booking(): void {
		console.log('booking!')
		//
	}

	// ------------------------------------------------------------------------ cancelBooking
	cancelBooking(): void {
		console.log('cancelBooking!')
	}

	// ------------------------------------------------------------------------ createOrder
	// from bookingPage
	async createOrder(order: Order): Promise<any> {
		order.invoice_creation = {
			//add invoice creation for the provider
			enabled: true,
		}

		console.log('create order:', order, '...')

		this.setPopupMessage(
			'info',
			'Neue Bestellung',
			'Bestellung wird erstellt...',
			0
		)
		let result = await this.fetch(
			'consumer/order',
			{ method: 'POST', body: JSON.stringify(order) },
			true
		)

		if (result?.orderId) {
			//success
			this.setPopupMessage(
				'ok',
				'Neue Bestellung',
				'Bestellung erfolgreich angelegt!'
			)
			order._completed = true
			await this.loadOrders() //if successfully reload all orders
		} else {
			this.setPopupMessage(
				'error',
				'Neue Bestellung',
				'Fehler beim Erstellen der Bestellung!'
			)
		}

		return result
	}

	// ------------------------------------------------------------------------ checkoutOrder
	// from Checkout page
	async checkoutOrder(orderId: string, returnParam?: string): Promise<any> {
		console.log('---checkoutOrder:', this.latestOrderId)
		let checkoutReq = {
			orderId: orderId,
			returnParam: returnParam,
		}
		let result = await this.fetch(
			'consumer/checkout',
			{ method: 'POST', body: JSON.stringify(checkoutReq) },
			true
		)
		return result
	}

	// ------------------------------------------------------------------------ calculatePrice
	async calculatePrice(mission: string, coordinates: any, purposes: any[]): Promise<any> {
		console.log('---calculatePrice:', mission)

		let coordinatesStr = typeof coordinates === 'string' ? coordinates : JSON.stringify(coordinates)

		let priceRequest = {
			mission: mission,
			coordinates: coordinatesStr,
			purposes: purposes,
		}

		let result = await this.fetch(
			'consumer/calculate-price',
			{ method: 'POST', body: JSON.stringify(priceRequest) },
			true
		)

		return result
	}


	// ------------------------------------------------------------------------ removeUpload
	async removeUpload(orderId: string, fileName: string): Promise<void> {
		console.log('---removeUpload', orderId, fileName, '...')

		this.setPopupDialog({
			type: 'warning', //ok | info | warning | error
			title: 'Auftrag stornieren?',
			text: 'Sind Sie sicher, dass Sie den Upload von "' + fileName + '" löschen wollen?',
			buttons: ['Ja', 'Nein'],
			time: 0,
			onAction: async (value: string): Promise<void> => {
				if (
					value === 'Ja'
				) {
					let result: boolean | null = await this.fetch(
						'provider/upload?orderId=' + orderId + '&fileName=' + fileName,
						'DELETE'
					)
					if (result) {
						this.setPopupMessage(
							'ok',
							'Upload löschen',
							'Der Upload von "' + fileName + '" wurde erfolgreich gelöscht!'
						)
					} else {
						this.setPopupMessage(
							'error',
							'Upload löschen',
							'Fehler beim Löschen das Uploads "' + fileName + '"!'
						)
					}
					await this.getUploads(orderId)
				}
			},
		} as PopupDialogProps)

		interface PopupDialogProps {
			type: IconColorType
			title: string
			text: string
			buttons: string[]
			time: number
			onAction: (value: string) => void | Promise<void>
		}

		// let result = await this.fetch(
		// 	'provider/upload?orderId=' + orderId + '&fileName=' + fileName,
		// 	'DELETE'
		// )

		// console.log('ok', result)

		// await this.getUploads(orderId)
	}


	// ------------------------------------------------------------------------ getUploads
	async getUploads(orderId: string): Promise<any[]> {
			const url = 'provider/uploads?orderId=' + orderId;
			console.log('[getUploads] SENDING REQUEST TO BE:', { url });
			let orderUploads = await this.fetch(url);
			console.log('[getUploads] Response from BE:', orderUploads);
			this.set({ orderUploads }); //upload uploads
			return orderUploads;
		}


	// ------------------------------------------------------------------------ getResults
	// from selectOrder if consumer
	async getResults(orderId: string): Promise<any[]> {
		let orderResults = await this.fetch(
			'consumer/results?orderId=' + orderId
		)
		if (!Array.isArray(orderResults)) orderResults = []
		console.log('---orderResults:', orderResults)
		this.set({ orderResults }) //upload uploads
		return orderResults
	}


	// ------------------------------------------------------------------------ loadStations
	async loadStations(): Promise<void> {
		const result = await this.fetch('provider/stations')
		if (result) {
			this.set({
				geoMeshes: result,
			})
		}
	}


	// ------------------------------------------------------------------------ reloadStationGeoJSON
	async reloadStationGeoJSON(stationUuid: string): Promise<void> {
		const result = await this.fetch('provider/station/' + stationUuid)

		if (result?.geoJson) {
			let geo = JSON.parse(result.geoJson)

			geo._key = `station-${stationUuid}-${Date.now()}`

			if (result.centerLatitude && result.centerLongitude) {
				geo._center = { lat: result.centerLatitude, lng: result.centerLongitude }
			}

			geo._uuid = stationUuid
			geo._isAllStations = false
			geo._isViewport = false

			const geoMeshes = this.get().geoMeshes
			let updatedMeshes = geoMeshes

			if (geoMeshes) {
				const stationIndex = geoMeshes.findIndex(s => s.uuid === stationUuid)
				if (stationIndex !== -1) {
					updatedMeshes = [...geoMeshes]
					updatedMeshes[stationIndex] = {
						...updatedMeshes[stationIndex],
						droneId: result.droneId,
						droneName: result.droneName,
						droneStationLatitude: result.droneLatitude,
						droneStationLongitude: result.droneLongitude,
						droneDiameter: result.droneDiameter,
					}

					delete updatedMeshes[stationIndex]._geo

					geo._name = updatedMeshes[stationIndex].name
				} else {
					geo._name = result.name
				}
			} else {
				geo._name = result.name
			}

			this.set({
				geoMeshes: updatedMeshes,
				geoJSON: geo
			})
		}
	}


	// ------------------------------------------------------------------------ loadDrones
	async loadDrones(): Promise<DroneInfo[]> {
		const drones = await this.fetch('provider/drones')
		if (drones && Array.isArray(drones)) {
			this.set({ drones })
			if (drones.length > 0 && !this.get().selectedDrone) {
				this.set({ selectedDrone: drones[0] })
			}
			return drones
		}
		return []
	}


	// ------------------------------------------------------------------------ selectDrone
	selectDrone(droneId: number): void {
		const drones = this.get().drones
		const drone = drones.find(d => d.id === droneId)
		if (drone) {
			this.set({ selectedDrone: drone })
		}
	}


	// ------------------------------------------------------------------------ loadAvailableDroneSns
	async loadAvailableDroneSns(): Promise<AvailableDroneSn[]> {
		const sns = await this.fetch('provider/drones/available-sns')
		if (sns && Array.isArray(sns)) {
			this.set({ availableDroneSns: sns })
			return sns
		}
		this.set({ availableDroneSns: [] })
		return []
	}


	// ------------------------------------------------------------------------ createDrone
	async createDrone(
		name: string,
		serialNumber: string,
		reachDiameter: number,
		flightSpeed: number,
		startStopTime: number,
		maxFlightTime: number
	): Promise<DroneInfo> {
		const result = await this.fetch('provider/drones', {
			method: 'POST',
			headers: { ...this.headers, 'Content-Type': 'application/json' },
			body: JSON.stringify({ name, serialNumber, reachDiameter, flightSpeed, startStopTime, maxFlightTime }),
		})
		await this.loadDrones()
		await this.loadAvailableDroneSns()
		await this.loadStations()
		await this._reloadCurrentStationGeoJSONIfAffected()
		return result
	}


	// ------------------------------------------------------------------------ updateDrone
	async updateDrone(
		id: number,
		name: string,
		reachDiameter: number,
		flightSpeed: number,
		startStopTime: number,
		maxFlightTime: number
	): Promise<DroneInfo> {
		const result = await this.fetch(`provider/drones/${id}`, {
			method: 'PUT',
			headers: { ...this.headers, 'Content-Type': 'application/json' },
			body: JSON.stringify({ name, reachDiameter, flightSpeed, startStopTime, maxFlightTime }),
		})
		await this.loadDrones()
		await this.loadStations()
		await this._reloadCurrentStationGeoJSONIfAffected()
		return result
	}


	// ------------------------------------------------------------------------ deleteDrone
	async deleteDrone(id: number): Promise<void> {
		await this.fetch(`provider/drones/${id}`, { method: 'DELETE' })
		await this.loadDrones()
		await this.loadAvailableDroneSns()
		await this.loadStations()
		await this._reloadCurrentStationGeoJSONIfAffected()
	}


	// ------------------------------------------------------------------------ _reloadCurrentStationGeoJSONIfAffected
	private async _reloadCurrentStationGeoJSONIfAffected(): Promise<void> {
		const currentGeoJSON = this.get().geoJSON
		if (currentGeoJSON?._uuid) {
			await this.reloadStationGeoJSON(currentGeoJSON._uuid)
		}
	}


	// ------------------------------------------------------------------------ loadAllStationsCells
	async loadAllStationsCells(): Promise<void> {
		const result = await this.fetch('provider/stations/all')
		if (result?.geoJson) {
			let geo = JSON.parse(result.geoJson)
			geo._key = `all-${Date.now()}`
			geo._center = { lat: result.centerLatitude, lng: result.centerLongitude }
			geo._name = 'Alle Flächen'
			geo._uuid = null
			geo._isAllStations = true
			geo._isViewport = false
			this.set('geoJSON', geo)
		} else {
			this.set('geoJSON', null)
		}
	}


	// ------------------------------------------------------------------------ loadViewportStationsCells
	async loadViewportStationsCells(topLeft: { lat: number, lng: number }, bottomRight: { lat: number, lng: number }): Promise<void> {
		const qs =
			`topLeftLatitude=${encodeURIComponent(topLeft.lat)}` +
			`&topLeftLongitude=${encodeURIComponent(topLeft.lng)}` +
			`&bottomRightLatitude=${encodeURIComponent(bottomRight.lat)}` +
			`&bottomRightLongitude=${encodeURIComponent(bottomRight.lng)}`

		const result = await this.fetch(`provider/stations/viewport?${qs}`)
		if (result?.geoJson) {
			let geo = JSON.parse(result.geoJson)
			geo._key = `viewport-${Date.now()}`
			geo._center = { lat: result.centerLatitude, lng: result.centerLongitude }
			geo._name = 'Viewport'
			geo._uuid = null
			geo._isAllStations = false
			geo._isViewport = true
			this.set('geoJSON', geo)
		} else {
			this.set('geoJSON', null)
		}
	}


	// ------------------------------------------------------------------------ loadAvailableGateways
	async loadAvailableGateways(): Promise<GatewayInfo[]> {
		const gateways = await this.fetch('provider/station/available-gateways')
		if (gateways && Array.isArray(gateways)) {
			this.set({ availableGateways: gateways })
			return gateways
		}
		this.set({ availableGateways: [] })
		return []
	}


	// ------------------------------------------------------------------------ snapToGrid
	async snapToGrid(lat: number, lng: number): Promise<{ lat: number, lng: number } | null> {
		try {
			const result = await this.fetch(`provider/mesh/snap?latitude=${lat}&longitude=${lng}`)

			if (result && result.latitude !== undefined && result.longitude !== undefined) {
				const snapped = {
					lat: result.latitude,
					lng: result.longitude
				}
				this.set({ snappedCenter: snapped })
				return snapped
			}
		} catch (error) {
			console.error('Error snapping coordinates:', error)
		}

		return null
	}


	// ------------------------------------------------------------------------ createStation
	async createStation(name: string, serialNumber: string, latLng: any, droneId?: number, droneLatLng?: any,
		fees?: {
			areaBasePrice: number
			decisionFee: number
			applicationFee: number
		}
	): Promise<void> {
		const selectedDrone = this.get().selectedDrone

		const areaBasePrice = fees?.areaBasePrice != null ? fees.areaBasePrice : 100
		const decisionFee = fees?.decisionFee != null ? fees.decisionFee : 0
		const applicationFee = fees?.applicationFee != null ? fees.applicationFee : 0

		let url =
			`provider/station?name=${encodeURIComponent(name)}` +
			`&serialNumber=${encodeURIComponent(serialNumber)}` +
			`&latitude=${latLng.lat}&longitude=${latLng.lng}` +
			`&colorStroke=%23000000&colorFill=%23ffffff` +
			`&defaultAreaBasePrice=${areaBasePrice}&defaultDecisionFee=${decisionFee}&defaultApplicationFee=${applicationFee}`

		if (!droneId) {
			const droneDiameter = selectedDrone?.droneType?.reachDiameter ?? 0
			url += `&droneDiameter=${droneDiameter}`
		}

		if (droneLatLng?.lat != null && droneLatLng?.lng != null) {
			url += `&droneStationLatitude=${droneLatLng.lat}&droneStationLongitude=${droneLatLng.lng}`
		}

		if (droneId) {
			url += `&droneId=${droneId}`
		}

		let result = await this.fetch(url, {
			method: 'POST',
		})

		if (result?.error) {
			this.setPopupMessage(
				'error',
				'Fehler beim Erstellen des Meshes',
				result.error
			)
		} else {
			this.setPopupMessage(
				'ok',
				'Mesh-Features erstellt',
				'Das Mesh wurden erfolgreich erstellt!'
			)

			this.set({
				geoMode: 'edit',
				geoCenter: null,
				snappedCenter: null,
			})
			this.loadStations()
		}

	}


	// ------------------------------------------------------------------------ updateStationDrone
	async updateStationDrone(stationUuid: string, droneId: number | null,
		fees?: {
			areaBasePrice: number
			decisionFee: number
			applicationFee: number
		}
	): Promise<void> {
		let url = `provider/station/${encodeURIComponent(stationUuid)}/drone`
		const params = new URLSearchParams()

		if (droneId != null) {
			params.append('droneId', droneId.toString())
		}

		if (fees) {
			const areaBasePrice = fees.areaBasePrice != null ? fees.areaBasePrice : 100
			const decisionFee = fees.decisionFee != null ? fees.decisionFee : 0
			const applicationFee = fees.applicationFee != null ? fees.applicationFee : 0

			params.append('defaultAreaBasePrice', areaBasePrice.toString())
			params.append('defaultDecisionFee', decisionFee.toString())
			params.append('defaultApplicationFee', applicationFee.toString())
		}

		if (params.toString()) {
			url += `?${params.toString()}`
		}

		const result = await this.fetch(url, { method: 'PUT' })

		if (result?.error) {
			this.setPopupMessage(
				'error',
				'Fehler beim Aktualisieren der Drohne',
				result.error
			)
			return
		}

		this.setPopupMessage(
			'ok',
			'Drohne aktualisiert',
			'Die Zuordnung der Drohne wurde erfolgreich geändert.'
		)

		await this.loadStations()

		const currentGeoJSON = this.get().geoJSON
		if (currentGeoJSON && currentGeoJSON._uuid === stationUuid) {
			await this.reloadStationGeoJSON(stationUuid)
		}
	}

	// ------------------------------------------------------------------------ moveStation
	async moveStation(stationUuid: string, lat: number, lng: number): Promise<void> {
		const qs =
			`latitude=${encodeURIComponent(lat)}` +
			`&longitude=${encodeURIComponent(lng)}`

		const result = await this.fetch(`provider/station/${encodeURIComponent(stationUuid)}/position?${qs}`, {
			method: 'PUT',
		})

		if (result?.error) {
			this.setPopupMessage(
				'error',
				'Fehler beim Verschieben der Station',
				result.error
			)
			return
		}

		this.setPopupMessage(
			'ok',
			'Station verschoben',
			'Die Position der Station wurde erfolgreich aktualisiert.'
		)

		await this.loadStations()
		await this.reloadStationGeoJSON(stationUuid)
	}


	// ------------------------------------------------------------------------ saveCells
	async saveCells(features: any): Promise<void> {
		let cellUpdates = Object.values(features).map((feature: any) => {
			const featureId = feature.id || ''
			const parts = featureId.split('_')
			const gridRow = parts.length === 2 ? parseInt(parts[0]) : 0
			const gridCol = parts.length === 2 ? parseInt(parts[1]) : 0

			return {
				gridRow,
				gridCol,
				areaBasePrice: feature.properties.areaBasePrice,
				decisionFee: feature.properties.decisionFee,
				applicationFee: feature.properties.applicationFee
			}
		})

		let result = await this.fetch('provider/cells', {
			method: 'POST',
			body: JSON.stringify(cellUpdates)
		})

		console.log('---result:', result)

		if (result?.error) {
			this.setPopupMessage(
				'error',
				'Fehler beim Speichern der Mesh-Features',
				result.error
			)
		} else {
			this.setPopupMessage(
				'ok',
				'Mesh-Features gespeichert',
				'Die Mesh-Features wurden erfolgreich gespeichert!'
			)

			const currentGeoJSON = this.get().geoJSON
			if (currentGeoJSON && currentGeoJSON._uuid) {
				await this.reloadStationGeoJSON(currentGeoJSON._uuid)
			}
		}
	}
	resetBookingData() {
		this.setBookingData({
			id: '',
			name: '',
			selectedServiceId: null,
			mission: 'Landwirtschaft',
			purposes: [],
			coordinates: {},
			dateFrom: null,
			dateTo: null,
			propertyNumber: null,
			fieldCultivation: null,
			area: '',
			information: '',
			price: 10000,
			resultTypes: [],
		})
	}

	// ------------------------------------------------------------------------ deleteStation
	async deleteStation(id: string): Promise<void> {

		let result = await this.fetch('provider/station/' + id, { method: 'DELETE' })
		if (result === true) {
			this.setPopupMessage(
				'ok',
				'Mesh gelöscht',
				'Das Mesh wurde erfolgreich gelöscht!'
			)
			this.set({
				geoCenter: null,
				snappedCenter: null,
				geoJSON: null, 		//reset geoJSON
				polygonRadius: FIXED_POLYGON_RADIUS,
			})
			this.loadStations() 		//reload stations

		} else {
			this.setPopupMessage(
				'error',
				'Fehler beim Löschen des Meshes',
				result.error
			)
		}
	}


}

// Create a new Store instance
const store = new Store()


if (typeof module !== 'undefined' && (module as any).hot) {
	(module as any).hot.dispose(() => {
		if (store.timerOrders) {
			console.log('---dispose store timerOrders ...')
			clearTimeout(store.timerOrders)
			store.timerOrders = null
		}
	})
}

export default store
